import type {
  DecodedBrowserAppointment,
  ParticipantAppointmentDetail,
} from '@/features/appointments/types';
import type { ParticipantAppointmentTransport } from '@/store/api/momentsParticipantAppointmentApi';

export const CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY =
  'member-chemistry-booking:current-attempt';

export const AMBIGUOUS_CHEMISTRY_BOOKING_MESSAGE =
  'The booking request was interrupted before its status was known.';

const MAX_STORED_ATTEMPT_BYTES = 8192;
const MAX_STORED_OWNERS = 8;
const CHEMISTRY_BOOKING_ATTEMPT_OWNERS_KEY = `${CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY}:owners`;

export interface ChemistryBookingCommand {
  coachId: string;
  idempotencyKey: string;
  kind: 'chemistry';
  memberId?: string;
  startAt: string;
  viewerTimezone: string;
}

export interface ChemistryBookingPresentation {
  coachAvatarUrl: string | null;
  coachFullName: string;
  durationMinutes: number;
  memberEmail: string;
  timezone: string;
}

export interface ChemistryBookingConfirmationCandidate {
  bookingId: string;
  endTime: string;
  meetUrl: string | null;
  startTime: string;
  status: 'confirmed' | 'pending' | 'rejected';
}

export interface StoredChemistryBookingAttempt {
  bookContractVersion?: 'appointment-book.v2';
  browserContractMarker?: 'browser-appointments.biweekly-booking-details-v2';
  calendarEventTitle?: string;
  canonicalCommand: string;
  command: ChemistryBookingCommand;
  confirmationInvalidated: boolean;
  confirmationCandidate: ChemistryBookingConfirmationCandidate | null;
  errorMessage: string | null;
  idempotencyKey: string;
  operationId: string | null;
  ownerKey: string;
  presentation: ChemistryBookingPresentation;
  retryAfterSeconds: number | null;
  status:
    | 'ambiguous'
    | 'confirmed'
    | 'error'
    | 'pending'
    | 'rejected'
    | 'review'
    | 'submitting';
  transport?: ParticipantAppointmentTransport;
  version: 1;
}

export const canonicalChemistryBookingCommand = (
  command: Omit<ChemistryBookingCommand, 'idempotencyKey'>,
) => JSON.stringify(command);

const isNonemptyBoundedString = (
  value: unknown,
  maxLength = 512,
): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= maxLength;

const isValidInstant = (value: unknown): value is string =>
  isNonemptyBoundedString(value) && Number.isFinite(Date.parse(value));

// Compare two ISO-8601 timestamps by instant, not wire string. The slot the
// Member selected is serialized with a timezone offset (e.g. "-04:00") but
// Backend/Moments respond with the UTC-normalized ("Z") equivalent — same
// moment, different string. Raw string equality would reject a legitimately
// matched confirmation candidate; use Date.parse for the compare so the
// persisted invariants agree with the confirmation matcher.
const sameInstant = (a: string, b: string): boolean =>
  Date.parse(a) === Date.parse(b);

const isConfirmationCandidate = (
  value: unknown,
): value is ChemistryBookingConfirmationCandidate => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ChemistryBookingConfirmationCandidate>;
  return (
    isNonemptyBoundedString(candidate.bookingId) &&
    isValidInstant(candidate.startTime) &&
    isValidInstant(candidate.endTime) &&
    Date.parse(candidate.endTime!) > Date.parse(candidate.startTime!) &&
    (candidate.meetUrl === null ||
      isNonemptyBoundedString(candidate.meetUrl, 2048)) &&
    ['confirmed', 'pending', 'rejected'].includes(candidate.status ?? '')
  );
};

const isStoredChemistryBookingAttempt = (
  value: unknown,
  // eslint-disable-next-line complexity -- One bounded storage trust boundary validates every field and status invariant.
): value is StoredChemistryBookingAttempt => {
  if (!value || typeof value !== 'object') return false;
  const attempt = value as Partial<StoredChemistryBookingAttempt>;
  const { transport } = value as { transport?: unknown };
  const command = attempt.command as
    Partial<ChemistryBookingCommand> | undefined;
  const presentation = attempt.presentation as
    Partial<ChemistryBookingPresentation> | undefined;
  if (
    attempt.version !== 1 ||
    (transport !== undefined &&
      transport !== 'rails' &&
      transport !== 'moments') ||
    !(
      (attempt.bookContractVersion === undefined &&
        attempt.browserContractMarker === undefined &&
        attempt.calendarEventTitle === undefined) ||
      (attempt.bookContractVersion === 'appointment-book.v2' &&
        attempt.browserContractMarker ===
          'browser-appointments.biweekly-booking-details-v2' &&
        typeof attempt.calendarEventTitle === 'string' &&
        attempt.calendarEventTitle.length > 0 &&
        Array.from(attempt.calendarEventTitle).length <= 1024)
    ) ||
    !isNonemptyBoundedString(attempt.ownerKey) ||
    !isNonemptyBoundedString(attempt.canonicalCommand, 4096) ||
    !isNonemptyBoundedString(attempt.idempotencyKey) ||
    typeof attempt.confirmationInvalidated !== 'boolean' ||
    !command ||
    !isNonemptyBoundedString(command.coachId) ||
    !isNonemptyBoundedString(command.idempotencyKey) ||
    command.idempotencyKey !== attempt.idempotencyKey ||
    command.kind !== 'chemistry' ||
    (command.memberId !== undefined &&
      !isNonemptyBoundedString(command.memberId)) ||
    !isNonemptyBoundedString(command.startAt) ||
    !isNonemptyBoundedString(command.viewerTimezone) ||
    canonicalChemistryBookingCommand({
      coachId: command.coachId!,
      kind: command.kind,
      ...(command.memberId ? { memberId: command.memberId } : {}),
      startAt: command.startAt!,
      viewerTimezone: command.viewerTimezone!,
    }) !== attempt.canonicalCommand ||
    !presentation ||
    (presentation.coachAvatarUrl !== null &&
      !isNonemptyBoundedString(presentation.coachAvatarUrl, 2048)) ||
    !isNonemptyBoundedString(presentation.coachFullName) ||
    typeof presentation.durationMinutes !== 'number' ||
    !Number.isFinite(presentation.durationMinutes) ||
    presentation.durationMinutes <= 0 ||
    presentation.durationMinutes > 1440 ||
    !isNonemptyBoundedString(presentation.memberEmail) ||
    !isNonemptyBoundedString(presentation.timezone) ||
    (attempt.operationId !== null &&
      !isNonemptyBoundedString(attempt.operationId)) ||
    (attempt.retryAfterSeconds !== null &&
      (typeof attempt.retryAfterSeconds !== 'number' ||
        !Number.isFinite(attempt.retryAfterSeconds) ||
        attempt.retryAfterSeconds < 0 ||
        attempt.retryAfterSeconds > 3600)) ||
    (attempt.errorMessage !== null &&
      !isNonemptyBoundedString(attempt.errorMessage, 2048)) ||
    (attempt.confirmationCandidate !== null &&
      !isConfirmationCandidate(attempt.confirmationCandidate)) ||
    ![
      'ambiguous',
      'confirmed',
      'error',
      'pending',
      'rejected',
      'review',
      'submitting',
    ].includes(attempt.status ?? '')
  ) {
    return false;
  }

  if (attempt.status === 'submitting') {
    return (
      attempt.operationId === null &&
      attempt.confirmationCandidate === null &&
      attempt.confirmationInvalidated === false &&
      attempt.errorMessage === null
    );
  }
  if (attempt.status === 'pending') {
    return (
      attempt.operationId !== null &&
      (attempt.confirmationCandidate === null ||
        (attempt.confirmationCandidate.status === 'pending' &&
          sameInstant(
            attempt.confirmationCandidate.startTime,
            command.startAt,
          ))) &&
      attempt.confirmationInvalidated === false &&
      attempt.errorMessage === null
    );
  }
  if (attempt.status === 'confirmed') {
    return (
      attempt.operationId !== null &&
      attempt.confirmationCandidate !== null &&
      attempt.confirmationCandidate.status === 'confirmed' &&
      sameInstant(attempt.confirmationCandidate.startTime, command.startAt) &&
      attempt.errorMessage === null
    );
  }
  if (attempt.status === 'ambiguous' || attempt.status === 'error') {
    return (
      attempt.operationId === null &&
      attempt.confirmationCandidate === null &&
      attempt.confirmationInvalidated === false &&
      attempt.errorMessage !== null
    );
  }
  if (attempt.status === 'review') {
    return (
      attempt.operationId !== null &&
      attempt.confirmationInvalidated === false &&
      attempt.errorMessage !== null
    );
  }
  return (
    attempt.operationId !== null &&
    attempt.confirmationCandidate === null &&
    attempt.confirmationInvalidated === false &&
    attempt.errorMessage !== null
  );
};

const storageKeyForOwner = (ownerKey: string) =>
  `${CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY}:owner:${encodeURIComponent(ownerKey)}`;

const loadStoredOwners = (): string[] => {
  const raw = window.sessionStorage.getItem(
    CHEMISTRY_BOOKING_ATTEMPT_OWNERS_KEY,
  );
  if (!raw || raw.length > MAX_STORED_ATTEMPT_BYTES) return [];
  const parsed: unknown = JSON.parse(raw);
  if (
    !Array.isArray(parsed) ||
    parsed.length > MAX_STORED_OWNERS ||
    !parsed.every((owner) => isNonemptyBoundedString(owner)) ||
    new Set(parsed).size !== parsed.length
  ) {
    return [];
  }
  return parsed;
};

const saveStoredOwners = (owners: string[]) => {
  window.sessionStorage.setItem(
    CHEMISTRY_BOOKING_ATTEMPT_OWNERS_KEY,
    JSON.stringify(owners),
  );
};

const removeStoredOwner = (ownerKey: string, owners: string[]) => {
  window.sessionStorage.removeItem(storageKeyForOwner(ownerKey));
  saveStoredOwners(owners.filter((owner) => owner !== ownerKey));
};

const readStoredAttempt = (raw: string | null) => {
  if (!raw || raw.length > MAX_STORED_ATTEMPT_BYTES) return null;
  const parsed: unknown = JSON.parse(raw);
  return isStoredChemistryBookingAttempt(parsed) ? parsed : null;
};

const persistChemistryBookingAttempt = (
  attempt: StoredChemistryBookingAttempt,
): boolean => {
  const encoded = JSON.stringify(attempt);
  if (
    encoded.length > MAX_STORED_ATTEMPT_BYTES ||
    !isStoredChemistryBookingAttempt(attempt)
  ) {
    return false;
  }
  let owners = loadStoredOwners();
  if (!owners.includes(attempt.ownerKey)) {
    while (owners.length >= MAX_STORED_OWNERS) {
      const evictableOwner = owners.find((owner) => {
        const stored = readStoredAttempt(
          window.sessionStorage.getItem(storageKeyForOwner(owner)),
        );
        return (
          !stored || stored.status === 'confirmed' || stored.status === 'error'
        );
      });
      if (!evictableOwner) return false;
      window.sessionStorage.removeItem(storageKeyForOwner(evictableOwner));
      owners = owners.filter((owner) => owner !== evictableOwner);
    }
    owners.push(attempt.ownerKey);
    saveStoredOwners(owners);
  }
  window.sessionStorage.setItem(storageKeyForOwner(attempt.ownerKey), encoded);
  try {
    window.sessionStorage.setItem(
      CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY,
      encoded,
    );
  } catch {
    // The per-owner record is authoritative; the rollout mirror is best effort.
  }
  return true;
};

export const loadChemistryBookingAttempt = (
  ownerKey: string,
): StoredChemistryBookingAttempt | null => {
  try {
    const ownerStorageKey = storageKeyForOwner(ownerKey);
    const ownerRaw = window.sessionStorage.getItem(ownerStorageKey);
    let parsed = readStoredAttempt(ownerRaw);
    if (ownerRaw && (!parsed || parsed.ownerKey !== ownerKey)) {
      removeStoredOwner(ownerKey, loadStoredOwners());
      parsed = null;
    }
    if (!parsed) {
      const legacyRaw = window.sessionStorage.getItem(
        CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY,
      );
      const legacyAttempt = readStoredAttempt(legacyRaw);
      if (legacyRaw && !legacyAttempt) {
        window.sessionStorage.removeItem(CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY);
      }
      if (legacyAttempt?.ownerKey === ownerKey) {
        parsed = legacyAttempt;
        persistChemistryBookingAttempt(legacyAttempt);
      }
    }
    if (!parsed) return null;
    if (parsed.status === 'submitting') {
      const recoveredAttempt: StoredChemistryBookingAttempt = {
        ...parsed,
        errorMessage: AMBIGUOUS_CHEMISTRY_BOOKING_MESSAGE,
        status: 'ambiguous',
      };
      persistChemistryBookingAttempt(recoveredAttempt);
      return recoveredAttempt;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const saveChemistryBookingAttempt = (
  attempt: StoredChemistryBookingAttempt,
): boolean => {
  try {
    return persistChemistryBookingAttempt(attempt);
  } catch {
    return false;
  }
};

// MEN-4915 (PR #1664 review): the booking confirmation is shown as soon as the
// admission response lands, so a member can leave while the provider operation
// is still pending. Nothing outside the chemistry page used to observe these
// attempts, which left the operation unobserved once the page unmounted: caches
// stayed stale and a later rejection or needs_reconciliation could never
// surface. Sessions/Upcoming uses this to adopt the pending operation, mirroring
// findViewerPendingBookingAttempt for bi-weekly series.
//
// Owner keys are `${coachId}:${memberId}:${viewerId}`, so the viewer is the
// last segment.
export const findViewerPendingChemistryAttempt = (
  viewerId: string,
): StoredChemistryBookingAttempt | null => {
  if (!viewerId) return null;
  try {
    return (
      loadStoredOwners()
        .filter((ownerKey) => ownerKey.split(':').pop() === viewerId)
        .map((ownerKey) => loadChemistryBookingAttempt(ownerKey))
        .find(
          (attempt) => attempt?.status === 'pending' && attempt.operationId,
        ) ?? null
    );
  } catch {
    return null;
  }
};

export const confirmedChemistryAttemptMatchesDetails = ({
  appointmentIds,
  appointments,
  attempt,
}: {
  appointmentIds: string[];
  appointments: (DecodedBrowserAppointment | ParticipantAppointmentDetail)[];
  attempt: StoredChemistryBookingAttempt;
}): boolean => {
  if (appointmentIds.length !== 1 || appointments.length !== 1) return false;
  const [appointmentId] = appointmentIds;
  const [appointment] = appointments;
  if (!appointmentId || !appointment) return false;
  return (
    appointment.id === appointmentId &&
    appointment.kind === 'chemistry' &&
    appointment.bookingStatus === 'confirmed' &&
    sameInstant(appointment.currentSchedule.startAt, attempt.command.startAt) &&
    (!attempt.confirmationCandidate ||
      (attempt.confirmationCandidate.bookingId === appointment.id &&
        sameInstant(
          attempt.confirmationCandidate.startTime,
          appointment.currentSchedule.startAt,
        )))
  );
};

export const clearChemistryBookingAttempt = (ownerKey: string): void => {
  if (!ownerKey) return;
  try {
    removeStoredOwner(ownerKey, loadStoredOwners());
    const mirroredAttempt = readStoredAttempt(
      window.sessionStorage.getItem(CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY),
    );
    if (mirroredAttempt?.ownerKey === ownerKey) {
      window.sessionStorage.removeItem(CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY);
    }
  } catch {
    // Navigation must not fail because storage is unavailable.
  }
};

export type ClearConfirmedChemistryAttemptResult =
  'cleared' | 'mismatch' | 'missing' | 'storage-unavailable';

export const clearConfirmedChemistryAttempt = ({
  idempotencyKey,
  ownerKey,
}: {
  idempotencyKey: string;
  ownerKey: string;
}): ClearConfirmedChemistryAttemptResult => {
  if (!ownerKey || !idempotencyKey) return 'missing';

  const matches = (attempt: StoredChemistryBookingAttempt | null) =>
    attempt?.ownerKey === ownerKey && attempt.idempotencyKey === idempotencyKey;
  const conflicts = (
    attempt: StoredChemistryBookingAttempt | null,
    primary = false,
  ) =>
    attempt !== null &&
    (primary || attempt.ownerKey === ownerKey) &&
    (attempt.ownerKey !== ownerKey ||
      attempt.idempotencyKey !== idempotencyKey);
  const ownerStorageKey = storageKeyForOwner(ownerKey);

  try {
    const primaryAttempt = readStoredAttempt(
      window.sessionStorage.getItem(ownerStorageKey),
    );
    const mirroredAttempt = readStoredAttempt(
      window.sessionStorage.getItem(CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY),
    );
    if (conflicts(primaryAttempt, true) || conflicts(mirroredAttempt)) {
      return 'mismatch';
    }
    if (!matches(primaryAttempt) && !matches(mirroredAttempt)) return 'missing';

    if (matches(mirroredAttempt)) {
      window.sessionStorage.removeItem(CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY);
      if (
        matches(
          readStoredAttempt(
            window.sessionStorage.getItem(
              CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY,
            ),
          ),
        )
      ) {
        return 'storage-unavailable';
      }
    }

    const currentPrimary = readStoredAttempt(
      window.sessionStorage.getItem(ownerStorageKey),
    );
    if (conflicts(currentPrimary, true)) return 'mismatch';
    if (matches(currentPrimary)) {
      window.sessionStorage.removeItem(ownerStorageKey);
      if (
        matches(
          readStoredAttempt(window.sessionStorage.getItem(ownerStorageKey)),
        )
      ) {
        return 'storage-unavailable';
      }
    }

    const currentMirror = readStoredAttempt(
      window.sessionStorage.getItem(CHEMISTRY_BOOKING_ATTEMPT_STORAGE_KEY),
    );
    const remainingPrimary = readStoredAttempt(
      window.sessionStorage.getItem(ownerStorageKey),
    );
    if (conflicts(remainingPrimary, true) || conflicts(currentMirror)) {
      return 'mismatch';
    }
    if (matches(remainingPrimary) || matches(currentMirror)) {
      return 'storage-unavailable';
    }

    const owners = loadStoredOwners();
    if (owners.includes(ownerKey)) {
      saveStoredOwners(owners.filter((owner) => owner !== ownerKey));
    }
    return 'cleared';
  } catch {
    return 'storage-unavailable';
  }
};
